// ADR-0158 (#0760) — las fotos y los PDF de UN mensaje del hilo de una
// consulta. El hilo trae sólo metadatos; las URL firmadas de las fotos se
// piden en lote para este mensaje (useOwnerInquiryAttachmentUrls) y la de
// un PDF, al hacer clic (abrirUrlFirmada: la pestaña se abre dentro del
// clic). Sin nombre original del archivo: «Documento PDF · 1,2 MB».
//
// Las miniaturas son `.evc__thumb` (la evidencia de una observación) con
// un <img> (sin `loading="lazy"`: el drawer es corto y una URL que vence
// mientras espera el scroll no se recupera sola). Si una falla —la URL
// venció—, se re-pide una sola vez. El visor lo monta el drawer: al
// abrirlo, si las URL tienen más de 60 s (duran 120), se re-firman antes,
// y se le pasa cómo re-firmar si una foto no carga mientras está abierto.
// Si el pedido de URL falla, se dice y se ofrece reintentar (sin eso las
// miniaturas quedaban grises y deshabilitadas sin explicación).
import { useRef, useState } from "react";
import type { OwnerInquiryAttachment } from "@bitacorapro/api-types";
import { getOwnerInquiryAttachmentUrls } from "../lib/api/endpoints/owner-inquiries";
import { mensajeDeError } from "../lib/api/error-message";
import { useOwnerInquiryAttachmentUrls } from "../lib/api/queries";
import { abrirUrlFirmada } from "../lib/abrirUrlFirmada";
import { urlsPorVencer } from "../lib/postventa/consultasList";
import type { LightboxPhoto } from "./Lightbox";
import { Icon } from "./icons";

const MB = 1024 * 1024;

function peso(bytes: number): string {
  return bytes >= MB
    ? `${(bytes / MB).toLocaleString("es-CL", { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("es-CL")} KB`;
}

export function AdjuntosMensaje({
  inquiryId,
  attachments,
  onVerFoto,
}: {
  inquiryId: string;
  attachments: readonly OwnerInquiryAttachment[];
  onVerFoto: (
    fotos: LightboxPhoto[],
    index: number,
    refirmar: () => Promise<LightboxPhoto[]>
  ) => void;
}) {
  const fotos = attachments.filter((a) => a.tipo === "foto");
  const pdfs = attachments.filter((a) => a.tipo === "pdf");
  const urls = useOwnerInquiryAttachmentUrls(
    inquiryId,
    fotos.map((f) => f.id)
  );
  const repedido = useRef(false);
  const [abriendo, setAbriendo] = useState<string | null>(null);
  const [errorPdf, setErrorPdf] = useState<{ id: string; texto: string } | null>(null);

  if (attachments.length === 0) return null;

  function fotoRota() {
    if (repedido.current) return;
    repedido.current = true;
    void urls.refetch();
  }

  async function abrir(a: OwnerInquiryAttachment) {
    if (abriendo) return;
    setAbriendo(a.id);
    setErrorPdf(null);
    try {
      await abrirUrlFirmada(() =>
        getOwnerInquiryAttachmentUrls(inquiryId, [a.id]).then((r) => r.urls[0]!.url)
      );
    } catch (err) {
      setErrorPdf({ id: a.id, texto: mensajeDeError(err, "No pudimos abrir el PDF.") });
    } finally {
      setAbriendo(null);
    }
  }

  const paraVisor = (mapa: ReadonlyMap<string, string> | undefined): LightboxPhoto[] =>
    fotos.flatMap((f, i) => {
      const url = mapa?.get(f.id);
      return url ? [{ url, alt: `Foto ${i + 1} de ${fotos.length}` }] : [];
    });

  async function refirmar(): Promise<LightboxPhoto[]> {
    const r = await urls.refetch();
    return paraVisor(r.data);
  }

  /** Abre el visor en la foto `id`; con URL viejas, re-firma antes. */
  async function verFoto(id: string) {
    let mapa = urls.data;
    if (urlsPorVencer(urls.dataUpdatedAt, Date.now())) {
      const r = await urls.refetch();
      if (r.data) mapa = r.data;
    }
    const url = mapa?.get(id);
    const lista = paraVisor(mapa);
    const idx = lista.findIndex((p) => p.url === url);
    if (idx >= 0) onVerFoto(lista, idx, refirmar);
  }

  return (
    <div className="adjuntos-msg">
      {fotos.length > 0 && urls.isError && !urls.isFetching && (
        <p className="auth-error" role="alert">
          No pudimos cargar las fotos.{" "}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void urls.refetch()}
          >
            Reintentar
          </button>
        </p>
      )}
      {fotos.length > 0 && (
        <div className="evc__thumbs">
          {fotos.map((f, i) => {
            const url = urls.data?.get(f.id);
            return (
              <button
                key={f.id}
                type="button"
                className="evc__thumb"
                aria-label={`Ver foto ${i + 1} de ${fotos.length}`}
                disabled={!url}
                onClick={() => void verFoto(f.id)}
              >
                {url && <img src={url} alt="" onError={fotoRota} />}
              </button>
            );
          })}
        </div>
      )}
      {pdfs.map((p) => (
        <div key={p.id} className="adjuntos-msg__pdf">
          <span className="adjuntos-msg__pdf-label">Documento PDF · {peso(p.sizeBytes)}</span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={abriendo !== null}
            aria-busy={abriendo === p.id || undefined}
            onClick={() => void abrir(p)}
          >
            <Icon.External /> {abriendo === p.id ? "Abriendo…" : "Abrir"}
            <span className="sr-only">, se abre en una pestaña nueva</span>
          </button>
          {errorPdf?.id === p.id && (
            <p className="auth-error" role="alert">
              {errorPdf.texto}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
